// Phase 9.2.B — Portal order history list (2026-05-11).
//
// Spec source: docs/apps/client-portal.md § "Order History".
//
// Columns: number, date, project, stage, total, in-hands date.
// Filters: stage (multi-select chips) and year (single-select chips) —
// honest chips only (CEO 2026-10-19): the server's `facets` list the years
// and stages this client really has orders in, so no chip leads to an empty
// list. No "Quote" chip — quotes have their own section now.
//
// Pagination uses the keyset cursor returned by the backend — "Load more"
// pattern (vs. numbered pages) keeps the UI simple and matches the
// listing's natural newest-first ordering.
import { useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  type OrderStage,
  type PortalOrderListResult,
  STAGE_LABEL,
  STAGE_PILL_CLASS,
  fetchPendingWebsiteOrders,
  fetchPortalOrders,
  orderStatusWords,
  formatDate,
  formatUsd,
} from "@/lib/portal-orders";
import { useAuth } from "@/auth/AuthProvider";
import { PortalLayout } from "@/components/PortalLayout";

/** Shared chip look — active = ink fill, idle = cream outline, a chip that
 *  would come back empty = faded and not clickable. */
function chipClass(active: boolean): string {
  return (
    "rounded-full border px-3 py-1 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 " +
    (active
      ? "border-rust-400 bg-ink text-white"
      : "border-cream-300 bg-white text-brown-400 enabled:hover:border-brown-300 enabled:hover:text-brown-500")
  );
}

export function OrdersListPage() {
  const { user } = useAuth();
  const [selectedStages, setSelectedStages] = useState<OrderStage[]>([]);
  const [year, setYear] = useState<number | null>(null);
  const [pages, setPages] = useState<PortalOrderListResult[]>([]);

  const filters = {
    stage: selectedStages.length > 0 ? selectedStages : undefined,
    year: year ?? undefined,
    limit: 20,
  };

  const { data, isLoading, isError, isPlaceholderData, refetch } = useQuery({
    queryKey: ["portal-orders", selectedStages, year],
    queryFn: () => fetchPortalOrders(filters),
    // Keep the chips (and rows) on screen while a new filter loads.
    placeholderData: keepPreviousData,
  });
  // The years / stages this client actually has — from the server, never a
  // made-up list. Not narrowed by the filters, so chips stay put.
  const stageChips = data?.facets?.stages ?? [];
  const yearChips = data?.facets?.years ?? [];
  // A chip is offered only if it gives a non-empty list WITH the other
  // filter on: a stage needs an order in the chosen year; a year needs an
  // order in one of the chosen stages. Active chips always stay clickable.
  const combos = data?.facets?.combos ?? [];
  const stageHasOrders = (s: OrderStage): boolean =>
    year === null || combos.some((c) => c.year === year && c.stage === s);
  const yearHasOrders = (y: number): boolean =>
    selectedStages.length === 0 ||
    combos.some((c) => c.year === y && selectedStages.includes(c.stage));

  // Reset cumulative pages whenever filters change.
  function toggleStage(s: OrderStage): void {
    setPages([]);
    setSelectedStages((prev) =>
      prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s],
    );
  }
  function setYearFilter(y: number | null): void {
    setPages([]);
    setYear(y);
  }

  async function loadMore(): Promise<void> {
    const cursor = (pages[pages.length - 1] ?? data)?.nextCursor;
    if (!cursor) return;
    const next = await fetchPortalOrders({ ...filters, cursor });
    setPages((prev) => [...prev, next]);
  }

  const allRows = [...(data?.rows ?? []), ...pages.flatMap((p) => p.rows)];
  const hasMore =
    !isPlaceholderData && (pages[pages.length - 1] ?? data)?.nextCursor != null;

  if (!user) return null;

  return (
    <PortalLayout>
        {/* Title + back link */}
        <div className="mb-10 flex items-center justify-between">
          <div>
            <Link
              to="/dashboard"
              className="mb-3 inline-block text-sm font-semibold text-ink underline-offset-2 hover:underline"
            >
              ← Dashboard
            </Link>
            <h1 className="font-display text-4xl font-bold tracking-tight">
              Your orders
            </h1>
            <p className="mt-2 text-brown-400">
              Everything you've ordered with us.
            </p>
          </div>
        </div>

        <PendingWebsiteOrders />

        {/* Filters — only chips that match at least one of your orders.
            A single stage or year is no choice at all, so it isn't offered. */}
        <div className="mb-8 flex flex-wrap items-center gap-3">
          {stageChips.length > 1 && (
            <>
              <span className="text-xs font-semibold uppercase tracking-wider text-brown-400">
                Stage
              </span>
              {stageChips.map((s) => (
                <button
                  key={s}
                  type="button"
                  aria-pressed={selectedStages.includes(s)}
                  onClick={() => toggleStage(s)}
                  disabled={!selectedStages.includes(s) && !stageHasOrders(s)}
                  className={chipClass(selectedStages.includes(s))}
                >
                  {STAGE_LABEL[s]}
                </button>
              ))}
            </>
          )}
          {yearChips.length > 1 && (
            <>
              <span
                className={
                  "text-xs font-semibold uppercase tracking-wider text-brown-400" +
                  (stageChips.length > 1 ? " ml-4" : "")
                }
              >
                Year
              </span>
              {yearChips.map((y) => (
                <button
                  key={y}
                  type="button"
                  aria-pressed={year === y}
                  onClick={() => setYearFilter(year === y ? null : y)}
                  disabled={year !== y && !yearHasOrders(y)}
                  className={chipClass(year === y)}
                >
                  {y}
                </button>
              ))}
            </>
          )}
          {(selectedStages.length > 0 || year !== null) && (
            <button
              type="button"
              onClick={() => {
                setPages([]);
                setSelectedStages([]);
                setYear(null);
              }}
              className="text-xs font-semibold text-brown-400 underline-offset-2 hover:text-brown-500 hover:underline"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-lg border border-cream-200 bg-white shadow-sm">
          {isLoading && (
            <div className="px-6 py-16 text-center text-brown-300">
              Loading your orders…
            </div>
          )}
          {isError && (
            <div className="px-6 py-12 text-center">
              <p className="mb-3 text-rust-500">
                We couldn't load your orders.
              </p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="text-sm font-semibold text-ink underline-offset-2 hover:underline"
              >
                Try again
              </button>
            </div>
          )}
          {!isLoading && !isError && allRows.length === 0 && (
            <div className="px-6 py-16 text-center">
              <p className="mb-2 font-display text-2xl font-bold tracking-tight text-brown-500">
                No orders yet
              </p>
              <p className="text-brown-400">
                {selectedStages.length > 0 || year !== null
                  ? "Try clearing your filters."
                  : "Your first order will appear here once your account manager sets it up."}
              </p>
            </div>
          )}
          {allRows.length > 0 && (
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-cream-200 text-xs font-semibold uppercase tracking-wider text-brown-300">
                  <th className="px-6 py-4">Order #</th>
                  <th className="px-6 py-4">Project</th>
                  <th className="px-6 py-4">Ordered</th>
                  <th className="px-6 py-4">In hands</th>
                  <th className="px-6 py-4 text-right">Total</th>
                  <th className="px-6 py-4">Stage</th>
                </tr>
              </thead>
              <tbody>
                {allRows.map((row) => (
                  <tr
                    key={row.id}
                    className="group border-b border-cream-100 last:border-b-0 transition-colors hover:bg-cream-50"
                  >
                    <td className="px-6 py-4">
                      <Link
                        to={`/orders/${row.id}`}
                        className="font-semibold text-ink underline-offset-2 group-hover:underline"
                      >
                        #{row.orderNumber}
                      </Link>
                      {row.customerPo && (
                        <div className="mt-0.5 text-xs text-brown-300">
                          PO {row.customerPo}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-brown-500">
                      {row.projectName}
                    </td>
                    <td className="px-6 py-4 text-sm text-brown-400">
                      {formatDate(row.orderDate)}
                    </td>
                    <td className="px-6 py-4 text-sm text-brown-400">
                      {formatDate(row.inHandsDate)}
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-brown-500 tabular-nums">
                      {formatUsd(row.totalAmount)}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={
                          "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold " +
                          STAGE_PILL_CLASS[row.statusStage]
                        }
                      >
                        {orderStatusWords(row.statusStage, row.statusState)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {hasMore && (
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => void loadMore()}
              className="rounded-md border border-cream-300 bg-white px-6 py-2.5 text-sm font-semibold text-brown-500 shadow-sm transition-colors hover:border-brown-300"
            >
              Load more
            </button>
          </div>
        )}
    </PortalLayout>
  );
}

/** Paid on the website, sales order not made yet — so the customer never
 *  sees an empty list right after paying. Hidden when there are none. */
function PendingWebsiteOrders() {
  const { data } = useQuery({ queryKey: ["portal-website-pending"], queryFn: fetchPendingWebsiteOrders });
  if (!data || data.orders.length === 0) return null;
  return (
    <section className="mb-10 rounded-md border border-cream-300 bg-white p-6" aria-label="Just received">
      <h2 className="mb-4 font-display text-xl font-bold">Just received</h2>
      <ul className="space-y-3">
        {data.orders.map((o) => (
          <li key={o.id} className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <span className="font-semibold">Order {o.reference}</span>
              <span className="text-brown-400"> · {o.items.map((i) => `${i.name} × ${i.quantity}`).join(", ")}</span>
              <p className="text-sm text-brown-400">{o.statusLabel}</p>
            </div>
            <span className="tabular-nums">{o.total ? formatUsd(o.total) : ""}{o.paidAt ? ` · paid ${formatDate(o.paidAt)}` : ""}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
